import {
  get,
  find,
  merge,
  reduce,
  remove,
  isEmpty,
  capitalize,
  cloneDeep,
  isPlainObject,
} from 'lodash';

import {
  contentRecordName,
  contentFieldLabel,
  contentFieldProps,
  contentFieldPath,
  contentModelRoot,
  findContentTitleFields,
} from './index';

import { objectToArray } from 'utils';
import { translateAppLink } from 'utils/app';
import { wordify, contentLookupKey } from 'shared/utils';

import {
  STANDARD_CONTENT_VIEWS,
  COLLECTION_MODELS,
  CONTENT_MODELS,
  CONTENT_MODEL_VIEWS,
} from 'constants/content';

/*
  Structure of content models is as follows
  --

  Data types:

  ContentModel {
    id: string,
    gid: string,
    collection: string,
    collectionModel: CollectionModel,
    childCollection: string,
    childCollectionModel: CollectionModel,
    root: boolean|string,
    fields: [ContentField],
    views: [ContentView],
  }

  ContentView {
    id: string,
    gid: string,
    type: string,
    label: string,
    fields: [ContentField],
    tabs: [ContentTab],
    filters: [ContentFilter],
    actions: [ContentAction],
    extra_actions: [ContentAction],
    bulk_actions: [ContentAction],
  }

  ContentTab {
    id: string,
    label: string,
    default: boolean,
    filter: string,
  }

  ContentFilter {
    id: string,
    label: string,
    default: boolean,
    filter: string,
  }

  ContentAction {
    id: string,
    label: string,
    default: boolean,
    filter: string,
  }

  ContentField {
    id: string,
    type: string,
    label: string,
    required: boolean,
    localized: boolean,
    default: any,
    options: [string],
    fields: [ContentField],
    func: (record) => any,
  }

  CollectionModel {
    collection: string,
    singular: string,
    plural: string,
    name_field: string,
    fields: [CollectionField],
  }
*/

/**
 * Normalize a content model including all of its fields, views, etc
 * @param {object} contentModel
 * @param {Record<string, object>} allCollectionModels
 * @param {Record<string, object>} appsEnabledById
 */
export function normalizeContentModel(
  contentModel,
  allCollectionModels,
  appsEnabledById = {},
) {
  const appId =
    contentModel.source_type === 'app' ? contentModel.source_id : undefined;
  const installedApp = appId ? appsEnabledById[appId] : undefined;

  const collection = collectionName({ name: contentModel.collection });

  let childCollection;
  let childCollectionModel;
  if (contentModel.root) {
    const childCollectionName = collectionName({
      name: `${collection}:${contentModel.root}`,
    });

    if (allCollectionModels[childCollectionName]) {
      childCollection = childCollectionName;
      childCollectionModel = allCollectionModels[childCollectionName];
    }
  }

  // Assign collection fields before normalizing fields and views
  const collectionModel = allCollectionModels[contentModel.collection];

  const modelProps = {
    ...contentModel,
    collection,
    collectionModel,
    ...(childCollection
      ? {
          childCollection,
          childCollectionModel,
        }
      : undefined),
    ...(installedApp
      ? {
          app: installedApp.app,
          app_id: installedApp.app_id,
        }
      : undefined),
  };

  const root = contentModelRoot(modelProps, collectionModel);

  const fields = (contentModel.fields || []).map((field) =>
    normalizeCollectionField(field, modelProps),
  );

  const views = (contentModel.views || []).map((view) =>
    normalizeContentView(modelProps, view, installedApp, allCollectionModels),
  );

  return {
    ...modelProps,
    root,
    fields,
    views,
  };
}

/**
 * Normalize a content view including all of its fields, actions, etc
 *
 * @param {object} contentModel
 * @param {object} contentView
 * @param {object} [installedApp]
 * @param {Record<string, object>} [allCollectionModels={}]
 */
function normalizeContentView(
  contentModel,
  contentView,
  installedApp,
  allCollectionModels = COLLECTION_MODELS,
) {
  const gid = contentView.gid || `${contentModel.id}.${contentView.id}`;
  const type =
    contentView.type || (contentView.id === 'list' ? 'list' : 'record');

  const collection = contentView.collection || contentModel.collection;
  const collectionModel =
    contentView.collectionModel || allCollectionModels[collection];
  const childCollection =
    contentView.childCollection || contentModel.childCollection;
  const childCollectionModel = allCollectionModels[childCollection];

  const label =
    contentView.label ||
    capitalize(`${contentView.id} ${collectionModel.singular}`);

  const viewProps = {
    ...contentView,
    gid,
    type,
    label,
    collection,
    collectionModel,
    contentModel,
    ...(childCollection
      ? {
          childCollection,
          childCollectionModel,
        }
      : undefined),
    ...(contentModel.app_id
      ? {
          app: contentModel.app,
          app_id: contentModel.app_id,
        }
      : undefined),
  };

  delete viewProps.extra_actions;
  delete viewProps.bulk_actions;

  const contentFields = contentView.fields || contentModel.fields;

  if (
    childCollection &&
    contentView?.parentContentModel?.collection !== collection
  ) {
    type === 'list'
      ? ensureParentListField(collectionModel, contentFields)
      : ensureParentRecordField(collectionModel, contentFields);
  }

  const fields = reduce(
    contentFields,
    (acc, field) => {
      const normalizedField =
        type === 'list'
          ? normalizeCollectionListField(field, contentModel)
          : normalizeCollectionField(field, contentModel);

      if (normalizedField) {
        acc.push(normalizedField);
      }

      return acc;
    },
    [],
  );

  const defaultConfigs =
    type === 'list'
      ? defaultCollectionListView(collectionModel, fields)
      : defaultCollectionRecordView(collectionModel, fields);

  const tabs = isEmpty(contentView.tabs)
    ? defaultConfigs.tabs || []
    : contentView.tabs.map((tab) => ({
        ...normalizeCollectionTab(tab, type, contentModel, fields),
        ...(tab.filter
          ? find(contentView.filters, { id: tab.filter }) || undefined
          : undefined),
      }));

  // Prepend default tab if not already present
  const hasDefault =
    tabs.find((tab) => tab.id === 'default') ||
    tabs.find((tab) => tab.default === true);

  if (!hasDefault) {
    if (type === 'list') {
      tabs.unshift({
        id: 'default',
        label: `All ${collectionModel.pluralLower}`,
        default: true,
      });
    } else if (tabs.length > 0) {
      tabs.unshift({
        id: 'default',
        label: `Details`,
      });
    }
  }

  const filters = isEmpty(contentView.filters)
    ? defaultConfigs.filters || []
    : contentView.filters.reduce((acc, filter) => {
        const normalizedFilter = normalizeCollectionFilter(
          filter,
          contentModel,
        );

        if (normalizedFilter) {
          acc.push(normalizedFilter);
        }

        return acc;
      }, []);

  const normalizeAction = (action) =>
    normalizeCollectionAction(
      action,
      collectionModel,
      contentModel,
      installedApp,
      viewProps,
    );

  const actions = contentViewActions(
    contentView.actions,
    defaultConfigs.actions,
  )
    .map(normalizeAction)
    .filter(Boolean);

  const extraActions = contentViewActions(
    contentView.extra_actions,
    defaultConfigs.extra_actions,
  )
    .map(normalizeAction)
    .filter(Boolean);

  // Bulk actions run an app function on the selected records
  const bulkActions = reduce(
    contentView.bulk_actions,
    (acc, bulkAction) => {
      const action =
        bulkAction.function &&
        !bulkAction.hidden &&
        normalizeAction(bulkAction);

      if (action) {
        acc.push({
          id: action.id,
          label: action.label,
          // Secondary like most bulk buttons, unless the app sets a style
          type: action.type || 'secondary',
          hint: action.hint,
          app: action.app,
          modal: 'AppAction',
          params: {
            app: action.app,
            action,
            context: { ...action.context, source: 'bulk' },
            modelName: collectionModel.pluralLower,
          },
        });
      }

      return acc;
    },
    [],
  );

  const query = collectionListViewQuery(
    isEmpty(contentView.query)
      ? defaultConfigs.query
      : normalizeContentViewQuery(contentView.query, contentModel),
    fields,
  );

  return {
    ...viewProps,
    fields,
    tabs,
    filters,
    actions,
    extraActions,
    bulkActions,
    query,
  };
}

const BUILT_IN_ACTIONS = ['new', 'save', 'delete'];

/**
 * View actions are added after the default actions. A view that lists a
 * built-in action (new, save, delete) lists all it keeps
 *
 * @param {Array<string|object>} [viewActions]
 * @param {Array<string|object>} [defaultActions=[]]
 * @returns {Array<string|object>}
 */
function contentViewActions(viewActions, defaultActions = []) {
  if (isEmpty(viewActions)) {
    return defaultActions;
  }

  const listsBuiltIn = viewActions.some((action) =>
    BUILT_IN_ACTIONS.includes(typeof action === 'string' ? action : action?.id),
  );

  return listsBuiltIn ? viewActions : [...defaultActions, ...viewActions];
}

function defaultContentModel(
  collectionModel,
  allCollectionModels,
  appsEnabledById = {},
) {
  const appId = collectionModel.app_id;
  const { collection } = collectionModel;
  const standardViews = standardContentViews(collection, allCollectionModels);

  const id = `default.${appId ? `${appId}.` : ''}${collection}`;
  const source_type = appId ? 'app' : standardViews ? 'standard' : 'custom';
  const source_id = appId ? appId : 'admin';

  const contentModel = {
    id,
    source_type,
    source_id,
    collection,
    root: appId || standardViews ? true : 'content',
    views: standardViews || [],
    collectionModel,
  };

  contentModel.fields = Object.values(collectionModel.fields || {}).reduce(
    (acc, field) => {
      const contentField = collectionModelFieldToContentField(
        field,
        contentModel,
        collectionModel,
      );

      if (contentField) {
        acc.push(contentField);
      }

      return acc;
    },
    [],
  );

  return normalizeContentModel(
    contentModel,
    allCollectionModels,
    appsEnabledById,
  );
}

/**
 * Generate default content models for all collections where one is not defined
 *
 * @param {Array<object>} contentModels
 * @param {Record<string, any>} allCollectionModels
 * @param {Record<string, any>} [appsEnabledById={}]
 * @returns {Array<object>}
 */
export function defaultContentModels(
  contentModels,
  allCollectionModels,
  appsEnabledById = {},
) {
  const defaultModels = [];

  for (const [collection, collectionModel] of Object.entries(
    allCollectionModels,
  )) {
    const contentModel = contentModels.find(
      (model) => model.collection === collection,
    );

    const standardModel = standardContentModel(
      collectionModel,
      allCollectionModels,
    );

    if (standardModel) {
      defaultModels.push(standardModel);
    }

    if (!standardModel && !contentModel) {
      defaultModels.push(
        defaultContentModel(
          collectionModel,
          allCollectionModels,
          appsEnabledById,
        ),
      );
    }
  }

  return defaultModels;
}

export function assignModelsToChildCollections(allContentModels) {
  for (const contentModel of allContentModels) {
    for (const field of contentModel.fields) {
      assignModelsToChildCollectionFields(field, contentModel);
    }

    for (const contentView of contentModel.views) {
      for (const field of contentView.fields) {
        assignModelsToChildCollectionFields(field, contentModel);
      }

      for (const contentTab of contentView.tabs) {
        for (const field of contentTab.fields || []) {
          assignModelsToChildCollectionFields(field, contentModel);
        }
      }
    }
  }
}

export function isFieldGroup(field) {
  switch (field.type) {
    case 'field_row':
    case 'field_group':
      return true;
    default:
      return false;
  }
}

function assignModelsToChildCollectionFields(field, contentModel) {
  if (isFieldGroup(field)) {
    for (const childField of field.fields || []) {
      assignModelsToChildCollectionFields(childField, contentModel);
    }

    return;
  }

  if (!field.child || !field.collectionModel) {
    return;
  }

  switch (field.type) {
    case 'collection': {
      field.contentModel = defaultChildCollectionModel(
        field.collectionModel,
        field.childCollectionModel,
        field.id,
      );

      // Assign default fields
      if (!field.fields?.length) {
        field.fields =
          field.contentModel?.fields ||
          defaultCollectionFields(
            field.childCollectionModel || field.collectionModel,
            field.contentModel,
          );
      }

      break;
    }

    case 'lookup': {
      // Assign properties from link collection
      if (field.childCollectionModel) {
        field.contentModel = defaultChildCollectionModel(
          field.collectionModel,
          field.childCollectionModel,
          field.id,
        );
      } else {
        field.contentModel = defaultChildCollectionModel(field.collectionModel);
      }

      if (!field.fields?.length) {
        field.fields =
          field.contentModel?.fields ||
          defaultCollectionFields(field.collectionModel, field.contentModel);
      }

      break;
    }

    default:
      return;
  }

  if (!field.contentModel) {
    return;
  }

  // Assign a list field definition for each field in a child collection
  if (field.collection) {
    for (const childField of field.fields || []) {
      if (!childField.childCollectionField) {
        childField.childCollectionField = normalizeCollectionListField(
          childField,
          field.contentModel,
        );
      }
    }
  }

  // Remove field references to the parent content model
  remove(
    field.fields,
    (field) =>
      field.childCollectionField?.collection === contentModel.collection,
  );

  assignViewToCollectionField(field);
}

function assignViewToCollectionField(field) {
  const parentCollection = field.parentContentModel?.collection;

  // Assign a view for use in child collection.
  // Map child collection fields and exclude undefined,
  // conditional, and parent reference fields.
  const childFields = filterContentModelFields(
    field.fields,
    (f) =>
      !f.conditions && f.id !== 'parent' && f.collection !== parentCollection,
  );

  // Get default tabs/filters from related content view
  const contentView = find(
    CONTENT_MODEL_VIEWS[field.childCollection || field.collection],
    (view) =>
      view.id === 'list' &&
      view.type === 'list' &&
      (!field.app_id || view.app_id === field.app_id),
  );

  const gid = `${
    contentView?.gid ||
    `${
      field.contentModel?.id || field.childCollection || field.collection
    }.list`
  }:${field.id}`;

  const viewProps = {
    id: 'list',
    type: 'list',
    gid,
    collection: field.collection,
    collectionModel: field.collectionModel,
    childCollection: field.childCollection,
    childCollectionModel: field.childCollectionModel,
    contentModel: field.contentModel,
    parentContentModel: field.parentContentModel,
    fields: childFields,
    tabs: contentView?.tabs,
    filters: contentView?.filters,
  };

  field.contentView = normalizeContentView(field.contentModel, viewProps);
}

function defaultChildCollectionModel(
  collectionModel,
  childCollectionModel,
  childFieldId,
) {
  if (
    !CONTENT_MODEL_VIEWS[collectionModel.collection] &&
    !CONTENT_MODELS[collectionModel.collection]
  ) {
    return null;
  }

  // Try to find a list view, app and then standard.
  // For $app child collections on standard models, collectionModel.app_id is
  // undefined; fall back to the app_id carried by the child collection model.
  const resolvedAppId = collectionModel.app_id ?? childCollectionModel?.app_id;

  const contentView =
    find(
      CONTENT_MODEL_VIEWS[collectionModel.collection],
      (view) => view.app_id === resolvedAppId && view.id === 'edit',
    ) ||
    find(
      CONTENT_MODEL_VIEWS[collectionModel.collection],
      (view) => view.standard && view.id === 'edit',
    );

  if (contentView?.fields?.length) {
    // Fall back to field of parent model
    if (childCollectionModel && childFieldId) {
      const parentModelFields = find(contentView.fields, {
        id: childFieldId,
      })?.fields;

      if (parentModelFields) {
        return {
          ...contentView.contentModel,
          childCollectionModel,
          childCollection: childCollectionModel.collection,
          fields: parentModelFields,
          views: [],
          actions: [],
          defaults: {},
        };
      }
    } else {
      return {
        ...contentView.contentModel,
        fields: contentView.fields,
      };
    }
  }

  // Then try to find a content model, app and then standard
  const contentModel =
    find(
      CONTENT_MODELS[collectionModel.collection],
      (model) => model.app_id === resolvedAppId,
    ) ||
    find(CONTENT_MODELS[collectionModel.collection], (model) => model.standard);

  if (contentModel?.fields?.length) {
    // Fall back to field of parent model
    if (childCollectionModel && childFieldId) {
      const parentModelFields = find(contentModel.fields, {
        id: childFieldId,
      })?.fields;

      if (parentModelFields) {
        return {
          ...contentModel,
          childCollectionModel,
          childCollection: childCollectionModel.collection,
          fields: parentModelFields,
          views: [],
          actions: [],
          defaults: {},
        };
      }
    } else {
      return contentModel;
    }
  }

  if (childCollectionModel) {
    const contentModel = defaultChildCollectionModel(childCollectionModel);

    if (contentModel?.fields) {
      return contentModel;
    }
  }

  return null;
}

/**
 * @param {object} [collectionModel]
 * @param {object} [contentModel]
 * @returns {object[]}
 */
function defaultCollectionFields(collectionModel, contentModel) {
  return reduce(
    collectionModel?.fields,
    (acc, field, key) => {
      const collectionField = normalizeCollectionField(
        { id: field.id || key },
        { ...(contentModel || undefined), collectionModel },
      );

      if (collectionField) {
        switch (collectionField.id) {
          case 'id':
          case 'parent':
          case 'parent_id':
            // Skip these fields
            break;

          default:
            acc.push(collectionField);
            break;
        }
      }

      return acc;
    },
    [],
  );
}

export function standardContentModel(collectionModel, allCollectionModels) {
  const { collection } = collectionModel;
  const standardViews = standardContentViews(collection, allCollectionModels);

  if (!standardViews) {
    return null;
  }

  const contentModel = {
    id: `standard.${collection}.admin`,
    source_type: 'standard',
    source_id: 'admin',
    standard: true,
    collection,
    root: true,
    views: standardViews,
  };

  return normalizeContentModel(
    {
      ...contentModel,
      fields: defaultCollectionFields(collectionModel, contentModel),
    },
    allCollectionModels,
  );
}

function standardContentViews(collection) {
  if (!STANDARD_CONTENT_VIEWS[collection]) {
    return null;
  }

  const contentViews = Array.isArray(STANDARD_CONTENT_VIEWS[collection])
    ? STANDARD_CONTENT_VIEWS[collection]
    : [
        // Default views are hard-coded and have list/edit/new pages
        { id: 'list' },
        { id: 'edit' },
        { id: 'new' },
      ];

  for (let i = 0; i < contentViews.length; i++) {
    contentViews[i] = {
      ...contentViews[i],
      collection,
      gid: `standard.${collection}.${contentViews[i].id}`,
      label: 'Standard view',
      standard: true,
    };
  }

  return contentViews;
}

/**
 * @param {object} collectionModel
 * @param {Array<object>} contentModels
 * @param {string} viewType
 * @param {Record<string, any>} [allCollectionModels={}]
 * @returns {object}
 */
export function defaultContentView(
  collectionModel,
  contentModels,
  viewType,
  allCollectionModels = {},
) {
  if (!collectionModel || !contentModels) {
    return null;
  }

  // Get unique field definitions from all content models
  const fields = Array.from(
    contentModels
      .reduce((map, model) => {
        for (const field of model.fields || []) {
          if (!map.has(field.id)) {
            map.set(field.id, normalizeCollectionField(field, model));
          }
        }

        return map;
      }, new Map())
      .values(),
  );

  const contentModel = {
    ...defaultContentModel(collectionModel, allCollectionModels),
    fields,
  };

  const view = {
    id: viewType,
    type: viewType,
    default: true,
    contentModel,
    ...(collectionModel.parent
      ? {
          collection: collectionModel.parent,
          collectionModel: allCollectionModels[collectionModel.parent],
          childCollection: collectionModel.collection,
          childCollectionModel: allCollectionModels[collectionModel.collection],
        }
      : {
          collection: collectionModel.collection,
          collectionModel: allCollectionModels[collectionModel.collection],
        }),
    // List view should only show "title fields"
    fields: viewType === 'list' ? findContentTitleFields(fields) : fields,
    ...(viewType === 'list'
      ? defaultCollectionListView(collectionModel, fields)
      : defaultCollectionRecordView(collectionModel, fields)),
  };

  if (collectionModel.parent) {
    viewType === 'list'
      ? ensureParentListField(collectionModel, view.fields)
      : ensureParentRecordField(collectionModel, view.fields);
  }

  // Add a name field if not contained in content fields
  const nameField = get(collectionModel.fields, collectionModel.name_field);
  if (
    nameField &&
    nameField.required &&
    !findContentField(
      view.fields,
      (f) => f.id === collectionModel.name_field && !f.root,
    )
  ) {
    view.fields.unshift({
      id: collectionModel.name_field,
      type: 'short_text',
      label: wordify(collectionModel.name_field),
      localized: true,
      required: nameField.required,
      func: (record) =>
        contentRecordName(
          collectionModel.collection,
          record[collectionModel.name_field],
        ),
    });
  }

  return normalizeContentView(
    contentModel,
    view,
    undefined,
    allCollectionModels,
  );
}

function defaultCollectionListView(collectionModel, fields) {
  const listView = {
    label: 'Default view',
    plural: collectionModel.plural,
    singular: collectionModel.singular,
    actions: ['new'],
  };

  return listView;
}

function defaultCollectionRecordView(collectionModel, fields) {
  const recordView = {
    label: 'Default view',
    plural: collectionModel.plural,
    singular: collectionModel.singular,
    actions: ['save'],
    extra_actions: ['delete'],
  };

  return recordView;
}

/**
 * @param {object} params
 * @param {string} params.name
 * @param {string} [params.namespace]
 * @param {string} [params.app_id]
 * @returns {string}
 */
export function collectionName({ namespace, name, app_id }) {
  const appApp = app_id ? `apps/${app_id}/` : '';
  const appNamespace = namespace ? `${namespace}/${name}` : name;

  return `${appApp}${appNamespace}`;
}

/**
 * Encode a collection path for use in admin URLs.
 * App models use '.' separator to avoid collision with underscores in app slugs.
 * Non-app models use '_' separator (legacy).
 */
export function collectionUrlParam(collection) {
  if (collection?.includes(':')) {
    return collection.split(':').map(collectionUrlParam).join(':');
  }

  if (collection?.startsWith('apps/')) {
    return collection.replace(/\//g, '.');
  }

  return collection?.replace(/\//g, '_');
}

/**
 * Decode a URL-encoded collection param back to its path form.
 * Handles both '.' (app models) and '_' (legacy/non-app) encoding.
 */
export function collectionFromUrlParam(param) {
  if (param?.includes(':')) {
    return param.split(':').map(collectionFromUrlParam).join(':');
  }

  if (param?.startsWith('apps.')) {
    return param.replace(/\./g, '/');
  }

  return param?.replace(/_/g, '/');
}

function collectionListViewQuery(viewQuery, fields) {
  const query = { ...(viewQuery || undefined) };

  const linkFields = fields.filter((f) => f.type === 'link');
  for (const field of linkFields) {
    if (isAppExtensionListLinkField(field) && !query.include?.[field.id]) {
      appendCollectionListExpand(query, field.path);
      continue;
    }

    query.include = query.include || {};
    if (!query.include[field.id]) {
      query.include[field.id] = {
        url: `/${field.model || field.collection}/{id}`,
        params: {
          id: contentLookupKey(field),
        },
      };
    }
  }

  const expandFields = fields.filter((f) => f.child);
  for (const field of expandFields) {
    appendCollectionListExpand(query, `${field.id}:1`);
  }

  return query;
}

function isAppExtensionListLinkField(field) {
  return Boolean(field.key_field && field.path?.startsWith('$app.'));
}

function appendCollectionListExpand(query, path) {
  if (!path) {
    return;
  }

  query.expand = query.expand || [];
  if (typeof query.expand === 'string') {
    query.expand = query.expand.split(',');
  }

  if (!query.expand.includes(path)) {
    query.expand.push(path);
  }
}

// For app content on standard collections, model metadata decides whether a
// list field is app-namespaced or native. If metadata is missing, keep the
// existing app-path fallback used by older app content normalization.
function collectionListFieldPath(field, contentModel) {
  if (!isAppStandardCollectionContent(contentModel) || !field) {
    return;
  }

  const fieldPath = collectionListModelFieldPath(field, contentModel);

  if (!contentModel.collectionModel?.$app?.[contentModel.app_id]) {
    return `$app.${contentModel.app?.slug_id || contentModel.app_id}.${
      field.id
    }`;
  }

  if (isAppExtensionListField(fieldPath, contentModel)) {
    return `$app.${
      contentModel.app?.slug_id || contentModel.app_id
    }.${fieldPath}`;
  }

  if (isNativeCollectionListField(fieldPath, contentModel)) {
    return fieldPath;
  }
}

const WHERE_LOGICAL_OPERATORS = new Set(['$and', '$or', '$nor']);

// Resolve where-clause field keys for app content applied to a standard
// collection: app extension fields get their $app.<app>.<field> data path,
// while native and already-qualified ($-prefixed) keys pass through unchanged
function normalizeAppContentWhere(where, contentModel) {
  if (!isPlainObject(where) || !isAppStandardCollectionContent(contentModel)) {
    return where;
  }

  const normalWhere = {};

  for (const key of Object.keys(where)) {
    if (WHERE_LOGICAL_OPERATORS.has(key) && Array.isArray(where[key])) {
      normalWhere[key] = where[key].map((clause) =>
        normalizeAppContentWhere(clause, contentModel),
      );
    } else if (key.startsWith('$')) {
      normalWhere[key] = where[key];
    } else {
      const fieldPath =
        collectionListFieldPath({ id: key }, contentModel) || key;

      normalWhere[fieldPath] = where[key];
    }
  }

  return normalWhere;
}

function normalizeContentViewQuery(viewQuery, contentModel) {
  if (!isPlainObject(viewQuery?.where)) {
    return viewQuery;
  }

  return {
    ...viewQuery,
    where: normalizeAppContentWhere(viewQuery.where, contentModel),
  };
}

function isAppStandardCollectionContent(contentModel) {
  return (
    contentModel?.app_id &&
    contentModel.app_id !==
      (contentModel.childCollectionModel ?? contentModel.collectionModel)
        ?.app_id
  );
}

function collectionListModelFieldPath(field, contentModel) {
  let fieldPath = contentFieldPath(field, contentModel);

  if (fieldPath.startsWith('$app.')) {
    fieldPath = fieldPath.split('.').slice(2).join('.');
  }

  return fieldPath;
}

function isAppExtensionListField(fieldPath, contentModel) {
  const appFields =
    contentModel?.collectionModel?.$app?.[contentModel.app_id]?.fields;

  if (!fieldPath) {
    return false;
  }

  return Boolean(appFieldAtPath(appFields, fieldPath));
}

function isNativeCollectionListField(fieldPath, contentModel) {
  if (!fieldPath) {
    return false;
  }

  return Boolean(
    appFieldAtPath(contentModel?.collectionModel?.fields, fieldPath),
  );
}

function appFieldAtPath(fields, path) {
  let field;
  let currentFields = fields;

  for (const part of path.split('.')) {
    field = currentFields?.[part];
    if (!field) {
      return;
    }
    currentFields = field.fields;
  }

  return field;
}

function collectionListLookupKey(field) {
  const lookupKey = contentLookupKey(field);
  const fieldRootPath = field.path?.split('.').slice(0, -1).join('.');

  if (fieldRootPath && lookupKey && !lookupKey.startsWith('$')) {
    return `${fieldRootPath}.${lookupKey}`;
  }

  return lookupKey;
}

/**
 * @param {object} [contentModel]
 * @returns {string[]}
 */
export function childCollectionExpandQuery(contentModel) {
  const expand = [];

  for (const field of contentModel?.fields || []) {
    const fieldProps = contentFieldProps(field);

    switch (fieldProps.type) {
      case 'lookup':
      case 'link': {
        expand.push(field.key_field ? field.id : `${field.id}:1`);
        break;
      }

      default: {
        if (fieldProps.child) {
          expand.push(`${field.id}:1`);
        }

        break;
      }
    }
  }

  return expand;
}

export function findCollectionModelField(field, contentModel) {
  if (!contentModel) {
    return null;
  }

  const collectionModel =
    contentModel.childCollectionModel ?? contentModel.collectionModel;
  if (!collectionModel) {
    return null;
  }

  let fieldPath = contentFieldPath(field, contentModel);

  if (!fieldPath) {
    return null;
  }

  // Remove $app prefix if exists
  if (fieldPath.startsWith('$app.')) {
    fieldPath = fieldPath.split('.').slice(2).join('.');
  }

  const modelFieldPath = fieldPath
    .split('.')
    .reduce(
      (acc, id) => (acc.length > 0 ? `${acc}.fields.${id}` : `fields.${id}`),
      '',
    );
  let modelField = get(collectionModel, modelFieldPath);

  // If the field is in an app, prepend the app id to the field path
  if (contentModel.app_id && collectionModel?.$app?.[contentModel.app_id]) {
    const appFieldPath = `$app.${contentModel.app_id}.${modelFieldPath}`;
    const appModelField = get(collectionModel, appFieldPath);

    if (appModelField) {
      if (modelField && modelField.type === appModelField.type) {
        modelField = merge(cloneDeep(modelField), appModelField);
      } else {
        modelField = appModelField;
      }

      // If the field name is not specified, then fill it in.
      if (modelField.type === 'collection' && !modelField.name) {
        const fieldName = modelFieldPath.split('.').pop();
        modelField.name = `${collectionModel.collection}:apps.${contentModel.app_id}.${fieldName}`;
      }
    }
  }

  if (!modelField) {
    return null;
  }

  return {
    ...modelField,
    name: modelField.name || modelFieldPath.split('.').pop(),
  };
}

/**
 * Returns the field that match id and root within fields list
 *
 * @param {string} fieldId
 * @param {string} fieldRoot
 * @param {object[]} fields
 * @returns {object | undefined}
 */
export function findContentModelField(fieldId, fieldRoot, fields) {
  if (!fieldId || !fields) {
    return;
  }

  for (const field of fields) {
    if (field.id === fieldId && field.root === fieldRoot) {
      return field;
    }

    if (field.fields) {
      const contentModelField = findContentModelField(
        fieldId,
        fieldRoot,
        field.fields,
      );

      if (contentModelField) {
        return contentModelField;
      }
    }
  }
}

/**
 * @param {object[]} fields
 * @param {(field: object) => boolean} filterFn
 * @param {object[]} [resultFields]
 * @returns {object[]}
 */
function filterContentModelFields(fields, filterFn, resultFields = []) {
  if (!Array.isArray(fields)) {
    return resultFields;
  }

  for (const field of fields) {
    if (field) {
      if (!isFieldGroup(field) && filterFn(field)) {
        resultFields.push(field);
      }

      if (field.fields) {
        filterContentModelFields(field.fields, filterFn, resultFields);
      }
    }
  }

  return resultFields;
}

/**
 * @param {object} field
 * @param {object} contentModel
 * @returns {object}
 */
function collectionModelFieldDefaultProps(field, contentModel) {
  const dataField = findCollectionModelField(field, contentModel);

  if (!dataField) {
    return {};
  }

  const collectionModel =
    contentModel.childCollectionModel ?? contentModel.collectionModel;

  const defaultProps =
    collectionModelFieldToContentField(field, contentModel, collectionModel) ||
    {};

  if (dataField.label) {
    defaultProps.label = dataField.label;
  }

  if (dataField.default !== undefined && !dataField.default?.$formula) {
    // Default, does not support formulas
    defaultProps.modelDefault = dataField.default;
  }

  // Enums - add options only for select fields
  if (dataField.enum && field?.type === 'select') {
    defaultProps.options = dataField.enum;
  }

  switch (dataField.type) {
    case 'array': {
      defaultProps.type = 'collection';

      if (dataField.label) {
        defaultProps.label = dataField.plural;
      }

      break;
    }

    case 'collection': {
      defaultProps.child = true;
      defaultProps.type = 'collection';
      defaultProps.parentContentModel = contentModel;

      if (dataField.label) {
        defaultProps.label = dataField.plural;
      }

      // Pass through link params from data model
      if (dataField.link) {
        defaultProps.link = dataField.link;
      }

      let childCollectionModel;

      if (dataField.parent) {
        // Standard path: parent is set by the server for native fields
        childCollectionModel =
          COLLECTION_MODELS[
            collectionModel.collection.replace(dataField.parent, dataField.name)
          ];
      }

      // Fallback for $app extension fields where parent is not set
      if (!childCollectionModel && dataField.name) {
        const fallbackKey = dataField.name;
        const candidate = COLLECTION_MODELS[fallbackKey];

        if (candidate?.parent === collectionModel.collection) {
          childCollectionModel = candidate;
        }
      }

      if (childCollectionModel) {
        defaultProps.collection = collectionModel.collection;
        defaultProps.collectionModel = collectionModel;
        defaultProps.childCollection = childCollectionModel.collection;
        defaultProps.childCollectionModel = childCollectionModel;
      } else {
        // Without a child collection model, ChildCollection would fall back
        // to querying the parent collection unfiltered and list unrelated
        // records, so leave collection unset to prevent rendering
        console.warn(
          `The "${field.id}" field will not be rendered because the "${dataField.name}" child collection model could not be resolved.`,
        );
      }

      break;
    }

    case 'link': {
      defaultProps.type = 'lookup';

      if (dataField.label) {
        defaultProps.label = dataField.plural;
      }

      // Assign properties from link collection
      switch (field.type) {
        case 'collection': {
          defaultProps.child = true;
          defaultProps.parentContentModel = contentModel;

          // Linked collections are readonly by default
          defaultProps.readonly = field.readonly ?? true;

          field.collection = dataField.model || field.collection;

          let childLinkCollectionModel;
          let linkCollectionModel = COLLECTION_MODELS[field.collection];

          if (linkCollectionModel) {
            // Reassign if targeting child collection
            if (linkCollectionModel.parent) {
              childLinkCollectionModel = linkCollectionModel;
              linkCollectionModel =
                COLLECTION_MODELS[linkCollectionModel.parent];
            }

            defaultProps.collection = linkCollectionModel.collection;
            defaultProps.collectionModel = linkCollectionModel;

            if (childLinkCollectionModel) {
              defaultProps.childCollection =
                childLinkCollectionModel.collection;
              defaultProps.childCollectionModel = childLinkCollectionModel;
            }
          } else {
            console.warn(
              `The "${field.id}" field will not be rendered because the "${field.collection}" collection model could not be resolved.`,
            );
          }

          break;
        }

        case 'lookup': {
          let collection = field.collection || dataField.model;

          if (contentModel.source_type === 'app') {
            let appCollectionId = '';

            // If child collection
            if (collection.includes(':')) {
              const [parentCollection, childCollection] = collection.split(
                ':',
                2,
              );

              const parentCollectionId = collectionName({
                name: parentCollection,
                app_id: contentModel.source_id,
              });

              if (!COLLECTION_MODELS[parentCollectionId]) {
                // When parent collection is not found, use app child collection of standard model
                appCollectionId = `${parentCollection}:apps.${contentModel.source_id}.${childCollection}`;
              }
            }

            if (!appCollectionId) {
              appCollectionId = collectionName({
                name: collection,
                app_id: contentModel.source_id,
              });
            }

            if (COLLECTION_MODELS[appCollectionId]) {
              collection = appCollectionId;
            }
          }

          defaultProps.collection = collection;
          break;
        }

        default:
          break;
      }

      // Set parent field label if undefined
      if (
        defaultProps.id === 'parent' &&
        !field.label &&
        collectionModel.parent
      ) {
        const parentModelCollection =
          COLLECTION_MODELS[collectionModel.fields?.parent?.model];
        defaultProps.label = parentModelCollection?.singular;
      }

      break;
    }

    default:
      break;
  }

  return defaultProps;
}

/**
 * @param {object} field
 * @param {object} contentModel
 * @returns {object}
 */
export function normalizeCollectionField(field, contentModel) {
  if (!field) {
    return;
  }

  const { id: fieldId, root: fieldRoot } = field;
  const { fields, source_type, collection } = contentModel || {};
  const contentModelField = findContentModelField(fieldId, fieldRoot, fields);

  if (!contentModelField && fieldId && source_type === 'app') {
    console.warn(
      `The "${fieldId}" field will not be rendered because it is not declared in the "${collection}" model.`,
    );
  }

  const fieldProps = {
    label: contentFieldLabel(field),
    ...field,
    ...contentFieldProps(field),
    ...(contentModelField ? contentFieldProps(contentModelField) : undefined),
  };

  const defaultProps = collectionModelFieldDefaultProps(
    fieldProps,
    contentModel,
  );

  const fieldResult = {
    ...defaultProps,
    ...fieldProps,
    id: fieldProps.id,
  };

  if (!fieldResult.type) {
    return;
  }

  if (isFieldGroup(fieldResult)) {
    fieldResult.fields = fieldResult.fields.map((f) =>
      normalizeCollectionField(f, contentModel),
    );
  }

  return fieldResult;
}

/**
 * Used for top-level content models depopulating parent lookup fields
 *
 * @param {object} contentModel
 */
export function getParentModelField(contentModel) {
  return {
    id: 'parent',
    type: 'lookup',
    collection: contentModel.collection,
  };
}

function ensureParentListField(collectionModel, fields) {
  if (!findContentField(fields, (f) => f.id === 'parent' && !f.root)) {
    // At the end
    fields.push({
      id: 'parent',
      label: collectionModel.singular,
      type: 'lookup',
      collection: collectionModel.collection,
    });
  }
}

function ensureParentRecordField(collectionModel, fields) {
  if (!findContentField(fields, (f) => f.id === 'parent' && !f.root)) {
    // At the beginning
    fields.unshift({
      id: 'parent',
      label: collectionModel.singular,
      type: 'lookup',
      collection: collectionModel.collection,
      required: true,
    });
  }
}

export function normalizeCollectionListField(field, contentModel) {
  const normalField = normalizeCollectionField(field, contentModel);

  // Action fields are buttons on a record, not list columns
  if (normalField?.type === 'action') {
    return null;
  }

  const fieldPath = collectionListFieldPath(normalField, contentModel);
  if (fieldPath) {
    normalField.path = fieldPath;
  }

  switch (normalField?.type) {
    case 'short_text':
      normalField.type = 'string';
      break;
    case 'long_text':
      normalField.type = 'string';
      break;
    case 'number':
      if (normalField.ui === 'currency') {
        normalField.type = 'currency';
      } else {
        normalField.type = 'string';
      }
      break;
    case 'select':
      normalField.type = 'string';
      break;
    case 'boolean':
      normalField.type = 'string';
      normalField.func =
        normalField.func ||
        ((record) =>
          get(record, normalField.path || normalField.id) ? 'Yes' : 'No');
      break;
    case 'date':
      normalField.type = 'date';
      break;
    case 'asset':
      if (normalField.ui === 'image') {
        normalField.type = 'image';
      }
      break;
    case 'tags':
      normalField.type = 'string';
      break;
    case 'color':
      normalField.type = 'string';
      break;
    case 'component':
      normalField.type = 'string';
      normalField.func =
        normalField.func ||
        ((record) => {
          const value = get(record, normalField.path || normalField.id);
          return value !== null && typeof value === 'object'
            ? undefined
            : value;
        });
      break;
    case 'icon':
      normalField.type = 'image';
      break;
    case 'lookup':
      normalField.type = 'link';
      normalField.key = collectionListLookupKey(normalField);

      normalField.url =
        normalField.link ||
        `${collectionLinkTarget(normalField)}/{${normalField.key}}`;

      normalField.func =
        normalField.func ||
        ((record) => {
          let value = normalField.path
            ? get(record, normalField.path)
            : undefined;
          if (value === undefined) {
            value = get(record, normalField.id);
          }
          return (
            value &&
            contentRecordName(
              normalField.model || normalField.collection,
              value,
            )
          );
        });
      break;
    case 'collection':
      normalField.type = 'string';
      normalField.func =
        normalField.func ||
        ((record) => {
          return get(
            record,
            normalField.child
              ? `${normalField.id}.count`
              : `${normalField.id}.length`,
            0,
          );
        });
      break;
    default:
      break;
  }
  return normalField;
}

function collectionModelFieldToContentField(
  field,
  contentModel,
  collectionModel,
) {
  const modelField = findCollectionModelField(field, contentModel);

  if (modelField) {
    const { name } = modelField;

    const label =
      field.label || collectionModel?.parent
        ? `${collectionModel.singular} ${wordify(name).toLowerCase()}`
        : wordify(name);

    const fieldProps = { ...field, label };

    switch (modelField.type) {
      case 'objectid':
        return null;

      case 'string': {
        if (modelField.multiline) {
          return { ...fieldProps, type: 'long_text' };
        }

        return { ...fieldProps, type: 'short_text' };
      }

      case 'bool':
        return { ...fieldProps, type: 'boolean', ui: 'toggle' };

      case 'int': {
        return { ...fieldProps, type: 'number' };
      }

      case 'float':
        return { ...fieldProps, type: 'number', ui: 'float' };

      case 'currency':
        return { ...fieldProps, type: 'number', ui: 'currency' };

      case 'date':
        return { ...fieldProps, type: 'date' };

      case 'link': {
        if (modelField.model) {
          return {
            ...fieldProps,
            type: 'lookup',
            model: modelField.model,
            key_field: modelField.key,
          };
        }

        break;
      }

      case 'array': {
        if (modelField.value_type !== 'object') {
          return null;
        }

        return {
          ...fieldProps,
          type: 'collection',
          fields: objectToArray(modelField.fields)
            .map((objectField) => {
              return collectionModelFieldToContentField(
                { ...field, id: `${field.id}.${objectField.id}` },
                contentModel,
                collectionModel,
              );
            })
            .filter(Boolean),
        };
      }

      case 'collection':
        return null;

      case 'object':
        return null;

      default:
        return null;
    }
  }
}

export function collectionLinkTarget(field) {
  let defaultCollection = field.model?.startsWith('apps/')
    ? field.model
    : field.collection || field.model || field.id;
  switch (defaultCollection) {
    case 'accounts':
      return '/customers';
    default:
      if (!STANDARD_CONTENT_VIEWS[defaultCollection]) {
        return `/collections/${collectionUrlParam(defaultCollection)}`;
      }
      break;
  }
  return `/${defaultCollection}`;
}

function normalizeCollectionFilter(filter, contentModel) {
  const fieldProps = contentFieldProps(filter);
  const normalFilter = {
    ...filter,
    id: fieldProps.id,
    label: contentFieldLabel(filter),
  };

  // For app content on standard collections, resolve the namespaced data path
  // ($app.<app>.<field>) so the query targets the extension field rather than
  // the display id, which is prefixed with the content source id downstream.
  const fieldPath = collectionListFieldPath(normalFilter, contentModel);
  if (fieldPath) {
    normalFilter.field = fieldPath;
  }

  switch (normalFilter.type) {
    case 'short_text':
    case 'long_text':
      normalFilter.type = 'text';
      break;
    case 'number':
      normalFilter.type = 'number';
      break;
    case 'select':
      normalFilter.type = 'select';
      break;
    case 'boolean':
      normalFilter.type = 'toggle';
      break;
    case 'date':
      normalFilter.type = 'date';
      break;
    case 'asset':
      break;
    case 'tags':
      normalFilter.type = 'tags';
      break;
    case 'color':
      normalFilter.type = 'color';
      break;
    case 'product_lookup':
      normalFilter.type = 'LookupProduct';
      // TODO more props
      break;
    case 'variant_lookup':
      normalFilter.type = 'LookupVariant';
      break;
    case 'category_lookup':
      normalFilter.type = 'LookupCategory';
      // TODO more props
      break;
    case 'customer_lookup':
      normalFilter.type = 'LookupCustomer';
      // TODO more props
      break;
    case 'lookup':
      normalFilter.type = 'LookupGeneric';
      normalFilter.model = fieldProps.collection || fieldProps.model;
      break;
    default:
      return null;
  }
  return normalFilter;
}

/**
 * @param {string|object} action
 * @param {object} collectionModel
 * @param {object} contentModel
 * @param {object} [installedApp]
 * @param {object} contentView normalized view props (id and type)
 * @returns {object|null} null for an app function action without the app
 */
function normalizeCollectionAction(
  action,
  collectionModel,
  contentModel,
  installedApp,
  contentView,
) {
  if (action.function) {
    return normalizeAppFunctionAction(
      action,
      contentModel,
      installedApp,
      contentView,
    );
  }

  const normalAction =
    typeof action === 'string'
      ? { id: action }
      : {
          ...action,
          internal: action.link?.startsWith('frontend://') ?? false,
          // Opens the app's frontend through a frontend access code
          ...(installedApp && { app_id: installedApp.app_id }),
        };

  if (!normalAction.link) {
    const view = contentModel.views?.find((view) => view.id === action.id);

    if (typeof view?.nav?.link === 'string') {
      normalAction.link = view.nav.link;

      if (!normalAction.target && view.nav.target) {
        normalAction.target = view.nav.target;
      }
    }
  }

  const hasCustomLink = typeof normalAction.link === 'string';

  const uri = hasCustomLink
    ? translateAppLink(normalAction, installedApp)
    : collectionLinkTarget({ collection: collectionModel.collection });

  if (hasCustomLink) {
    normalAction.link = uri;
  }

  if (installedApp?.app_id) {
    normalAction.app_id = installedApp.app_id;

    if (
      hasCustomLink &&
      !normalAction.internal &&
      (!normalAction.target || normalAction.target === 'self')
    ) {
      normalAction.external = action.link;
    }
  }

  const { singularLower } = collectionModel;

  switch (normalAction.id) {
    case 'new':
      normalAction.label = normalAction.label || `New ${singularLower}`;
      normalAction.link = normalAction.link || `${uri}/new`;
      break;
    case 'save':
      normalAction.label = normalAction.label || `Save ${singularLower}`;
      normalAction.submit = true;
      break;
    case 'delete':
      normalAction.label = normalAction.label || `Delete ${singularLower}`;
      normalAction.className = 'danger';
      normalAction.delete = true;
      break;
    default:
      normalAction.label = normalAction.label || wordify(normalAction.id);
      break;
  }

  return normalAction;
}

/**
 * Action that runs an app function, with the context sent as $action
 *
 * @param {object} action
 * @param {object} contentModel
 * @param {object} [installedApp]
 * @param {object} contentView normalized view props (id and type)
 * @returns {object|null}
 */
function normalizeAppFunctionAction(
  action,
  contentModel,
  installedApp,
  contentView,
) {
  if (!installedApp) {
    return null;
  }

  const { conditions, ...appAction } = action;

  return {
    ...appAction,
    // List actions have no record to evaluate conditions against
    ...(contentView.type !== 'list' && conditions && { conditions }),
    label: action.label || wordify(action.id),
    app: installedApp.app,
    context: {
      source: contentView.type,
      content_id: contentModel.id,
      view: contentView.id,
    },
    showAlways: true,
  };
}

/**
 * @param {object} tab
 * @param {string} type
 * @param {object} contentModel
 * @param {object[]} contentViewFields
 */
export function normalizeCollectionTab(
  tab,
  type,
  contentModel,
  contentViewFields,
) {
  const tabFields =
    tab.fields?.length > 0
      ? (tab.fields || [])
          // Normalize the new fields
          .map((field) => {
            // Only match by id when field has one (field_row/field_group have no id)
            const contentViewField =
              field.id && find(contentViewFields, { id: field.id });
            if (contentViewField) {
              return { ...contentViewField, ...field };
            }
            return type === 'list'
              ? normalizeCollectionListField(field, contentModel)
              : normalizeCollectionField(field, contentModel);
          })
          .filter(Boolean)
      : undefined;

  const normalTab = {
    ...tab,
    label: tab.label || wordify(tab.id),
    fields: tabFields,
  };

  // Tab queries are consumed as a where clause (pushed into where.$and when
  // fetching the collection), so unwrap content query expressions declared as
  // { where: {...} } and resolve app extension field keys to $app data paths
  if (isPlainObject(normalTab.query)) {
    const where = isPlainObject(normalTab.query.where)
      ? normalTab.query.where
      : normalTab.query;

    normalTab.query = normalizeAppContentWhere(where, contentModel);
  }

  return normalTab;
}

/**
 * @template T
 * @param {T[]} fields
 * @param {string | ((field: T) => boolean)} idOrFilter
 * @returns {T | undefined}
 */
export function findContentField(fields, idOrFilter) {
  let foundField;

  find(fields, (field) => {
    switch (typeof idOrFilter) {
      case 'string': {
        if (field.id === idOrFilter) {
          foundField = field;
          return true;
        }

        break;
      }

      case 'function': {
        if (idOrFilter(field)) {
          foundField = field;
          return true;
        }

        break;
      }

      default:
        break;
    }

    if (Array.isArray(field.fields)) {
      foundField = findContentField(field.fields, idOrFilter);

      if (foundField !== undefined) {
        return true;
      }
    }

    return false;
  });

  return foundField;
}
